// @ts-check

import { useCallback, useContext, useMemo } from "react";
import { AppContext } from "src/AppContext";
import { ALL_SUPERVISOR_PERMISSIONS } from "src/pages/UserDashboard/WorkPackets/WorkPacketPermission";

const usePermissions = () => {
  const { permissions } = useContext(AppContext);

  const hasPermission = useCallback(
    /**
     * @param {string[]} required_permissions
     * @param {boolean} require_all
     */
    (required_permissions, require_all = false) => {
      let requires = new Map();
      let remaining_requirements = required_permissions;

      for (const [_index, required_permission] of required_permissions.entries()) {
        const components = required_permission.split(":");
        let permission = "";
        components.forEach(component => {
          if (!component.includes("*")) {
            permission = permission + component + ":";
            requires.set(permission + "*", required_permission);
          } else {
            permission = permission + "*:";
          }
        });
      }

      for (const [_index, permission] of permissions.entries()) {
        const permission_components = permission.split(":");

        // All requirements met
        if (remaining_requirements.length === 0) {
          return true;
        }

        // Complete Match of Permission and Requirement
        if (remaining_requirements.includes(permission)) {
          if (require_all) {
            remaining_requirements = remaining_requirements.filter(r => r !== permission);
            continue;
          } else {
            return true;
          }
        }

        for (const [_index, remaining_requirement] of remaining_requirements.entries()) {
          const components = remaining_requirement.split(":");
          let match = true;

          for (const [index, accept_component] of components.entries()) {
            if (accept_component === "*" || permission_components[index] === "*") {
              continue;
            }

            if (permission_components[index] !== accept_component) {
              match = false;
              break;
            }
          }

          if (match) {
            if (require_all) {
              remaining_requirements = remaining_requirements.filter(r => r !== remaining_requirement);
            } else {
              return true;
            }
          }
        }

        for (const [require, required_permission] of requires.entries()) {
          // No longer required (already met)
          if (!required_permissions.includes(required_permission)) {
            continue;
          }

          // Wildcard Match
          if (permissions.includes(require)) {
            if (require_all) {
              remaining_requirements = remaining_requirements.filter(r => r !== required_permission);
            } else {
              return true;
            }
          }
        }
      }

      if (require_all) {
        return remaining_requirements.length === 0;
      } else {
        return false;
      }
    },
    [permissions],
  );

  const isAdmin = useMemo(() => hasPermission(["admin:*"]), [hasPermission]);
  const isSupervisor = useMemo(() => hasPermission(ALL_SUPERVISOR_PERMISSIONS), [hasPermission]);

  const memoized = useMemo(
    () => ({
      hasPermission,
      isAdmin,
      isSupervisor,
      permissions,
    }),
    [hasPermission, isAdmin, isSupervisor, permissions],
  );

  return memoized;
};

export default usePermissions;
